import { useState } from "react";
import { TimeseriesStateManager } from "~/modules/visualization/components/Timeseries/TimeseriesStateManager";
import { TimeSeriesFetcher } from "~/modules/visualization/components/Timeseries/TimeSeriesFetcher";
import { TimeseriesCard } from "~/modules/visualization/components/Timeseries/TimeseriesCard";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3";
import {
    ChartTypes,
    TimeSeriesItem,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import { MEASURE_IDS } from "~/consts";
import { RWA_COLORS } from "~/styles/colors";
import { ColumnSpanConfig, DistributionType, DistributionTypes } from "~/types";
import {
    TimeseriesSegmentSelectProps,
    VisualizationMode,
    VisualizationModeTypes,
} from "./types";
import { DistributionSelectProps } from "../MetricTimeseriesChart/types";
import { applyDistributionFilters } from "./applyDistributionFilters";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { RenderWhenVisible } from "~/components/RenderWhenVisible";
import { mergeApiQueries } from "~/modules/filters/mergeApiQueries";
import { getRandomNumberInRange } from "~/utils/getRandomNumberInRange";

// // // //

export interface Props {
    title: string;
    modes: VisualizationMode[];
    columnSpanConfig?: ColumnSpanConfig;
    distributionSelect?: DistributionSelectProps;
    segmentSelect?: TimeseriesSegmentSelectProps;
    /** Reshapes fetched timeseries data before it's mapped to chart series
     * (e.g. the stocks page merging the ETF + ETP series into one). */
    transformTimeseriesData?: (data: TimeSeriesItem[]) => TimeSeriesItem[];
}

/**
 * VisualizationModeCard
 * Encapsulates markup for the Treasury TimeSeries card and logic for toggling between area/line chart
 */
export function VisualizationModeCard(props: Props) {
    const {
        title,
        modes,
        distributionSelect,
        segmentSelect,
        transformTimeseriesData,
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
    } = props;

    // Distribution type state
    const defaultDistributionType = distributionSelect?.distributed.enabled
        ? DistributionTypes.Distributed
        : DistributionTypes.All;

    const [selectedPlatformType, setSelectedPlatformType] = useState(
        defaultDistributionType,
    );

    // Stablecoins state
    const defaultIncludeStablecoins =
        distributionSelect?.stablecoins?.active ?? false;

    const [includeStablecoins, setIncludeStablecoins] = useState(
        defaultIncludeStablecoins,
    );

    // Derive UI visibility from distributionSelect config
    const hideStablecoinsToggle = distributionSelect?.stablecoins
        ? !distributionSelect.stablecoins.enabled
        : true;

    // Segment select state (e.g. the stocks page's ETF/ETP vs. Equity/Stock control)
    const [selectedSegment, setSelectedSegment] = useState(
        segmentSelect?.defaultValue,
    );

    return (
        <TimeseriesStateManager modes={modes}>
            {({
                controls,
                mode,
                aggregateQuery,
                appliedQuery,
                dataExportControls,
            }) => {
                // Apply distribution filters when distributionSelect is provided
                const distributionFilteredQuery = distributionSelect
                    ? applyDistributionFilters({
                          query: appliedQuery,
                          selectedPlatformType,
                          includeStablecoins,
                          endpoint: mode.activeMode.endpoint,
                      })
                    : appliedQuery;

                // Layer on the selected segment's filters when segmentSelect is provided
                const selectedSegmentOption = segmentSelect?.options.find(
                    (option) => option.value === selectedSegment,
                );

                const filteredQuery = selectedSegmentOption
                    ? selectedSegmentOption.filters.reduce(
                          (query, filter) =>
                              mergeApiQueries({
                                  baseQuery: query,
                                  query: { filter },
                              }),
                          distributionFilteredQuery,
                      )
                    : distributionFilteredQuery;

                return (
                    <TimeSeriesFetcher
                        query={filteredQuery}
                        endpoint={mode.activeMode.endpoint}
                    >
                        {({ data, loading }) => {
                            const chartData = transformTimeseriesData
                                ? transformTimeseriesData(data)
                                : data;

                            const isLoading = loading || mode.loading;

                            // "Show % of Total" is meaningless with a single
                            // series — hide the toggle and render unstacked.
                            // Only kicks in once data has settled so the toggle
                            // doesn't flicker during refetches.
                            const hideStackedControls =
                                !isLoading && chartData.length <= 1;

                            const defaultSeriesChartType =
                                mode.activeMode.visualizationConfig
                                    ?.seriesChartType ??
                                (mode.activeMode.type ===
                                VisualizationModeTypes.apy
                                    ? ChartTypes.line
                                    : ChartTypes.area);

                            // NOTE - this should be handled inside of timeseries fetch + mode configuration, if possible
                            let series: TimeSeriesView[] = chartData.map(
                                (each) => {
                                    return {
                                        item: each,
                                        enabled: true,
                                        chartType: defaultSeriesChartType,
                                    };
                                },
                            );

                            // Change colors for mint/burn chart
                            if (
                                mode.activeMode.type ===
                                VisualizationModeTypes.mintBurnVolume
                            ) {
                                series = chartData
                                    .map((each) => {
                                        if (
                                            each.measure.id ===
                                            MEASURE_IDS.AdjustedDailyBurnsDollar
                                        ) {
                                            return {
                                                ...each,
                                                group: {
                                                    ...each.group,
                                                    name: "Daily Burns (Dollar)",
                                                    color: RWA_COLORS.red[500],
                                                },
                                            };
                                        }
                                        return {
                                            ...each,
                                            group: {
                                                ...each.group,
                                                name: "Daily Mints (Dollar)",
                                                color: RWA_COLORS.emerald[500],
                                            },
                                        };
                                    })
                                    .map((each) => {
                                        return {
                                            item: each,
                                            enabled: true,
                                            chartType: ChartTypes.column,
                                            // Invert the yAxisValues for burns
                                            invertYAxis: [
                                                MEASURE_IDS.DailyMintsDollar,
                                                MEASURE_IDS.AdjustedDailyMintsDollar,
                                            ].includes(each.measure.id)
                                                ? undefined
                                                : true,
                                        };
                                    });
                            }

                            // Set ChartType to column
                            if (
                                mode.activeMode.type ===
                                VisualizationModeTypes.column
                            ) {
                                series = chartData.map((each) => {
                                    return {
                                        item: each,
                                        enabled: true,
                                        chartType: ChartTypes.column,
                                    };
                                });
                            }

                            return (
                                <TimeseriesCard
                                    title={title}
                                    height={
                                        mode.activeMode.visualizationConfig
                                            ?.height
                                    }
                                    apiQuery={filteredQuery}
                                    loading={isLoading}
                                    empty={chartData.length === 0}
                                    modeControls={mode}
                                    controls={{
                                        ...controls,
                                        stacked: {
                                            ...controls.stacked,
                                            enabled:
                                                controls.stacked.enabled &&
                                                !hideStackedControls,
                                        },
                                    }}
                                    exportData={dataExportControls}
                                    colSpans={columnSpanConfig}
                                    aggregateQueryControls={{
                                        groupBy: aggregateQuery.groupBy,
                                        interval: aggregateQuery.interval,
                                        aggregateFunction:
                                            aggregateQuery.aggregateFunction,
                                    }}
                                    apiTools={{
                                        enabled:
                                            mode.activeMode.apiTools?.enabled ||
                                            false,
                                        baseUrl:
                                            mode.activeMode.apiTools?.baseUrl ||
                                            "",
                                    }}
                                    distributionSelect={
                                        distributionSelect
                                            ? {
                                                  value: selectedPlatformType,
                                                  onChange:
                                                      setSelectedPlatformType as (
                                                          v: DistributionType,
                                                      ) => void,
                                                  enabledOptions: {
                                                      distributed:
                                                          distributionSelect
                                                              .distributed
                                                              .enabled,
                                                      represented:
                                                          distributionSelect
                                                              .represented
                                                              .enabled,
                                                  },
                                              }
                                            : undefined
                                    }
                                    stablecoinsToggle={
                                        distributionSelect &&
                                        !hideStablecoinsToggle
                                            ? {
                                                  checked: includeStablecoins,
                                                  onChange:
                                                      setIncludeStablecoins,
                                              }
                                            : undefined
                                    }
                                    segmentSelect={
                                        segmentSelect
                                            ? {
                                                  label: segmentSelect.label,
                                                  options:
                                                      segmentSelect.options,
                                                  value:
                                                      selectedSegment ??
                                                      segmentSelect.defaultValue,
                                                  onChange: setSelectedSegment,
                                              }
                                            : undefined
                                    }
                                >
                                    <RenderWhenVisible
                                        // Unmount the Highcharts instance when
                                        // the card is scrolled offscreen —
                                        // pages like /asset-classes render many
                                        // of these at once. 300 matches the
                                        // TimeSeriesChart height default.
                                        placeholderHeight={
                                            mode.activeMode.visualizationConfig
                                                ?.height ?? 300
                                        }
                                    >
                                        <WindowResizeObserver
                                            delay={getRandomNumberInRange({
                                                min: 800,
                                                max: 1500,
                                            })}
                                            defaultPlaceholderHeight={
                                                mode.activeMode
                                                    .visualizationConfig?.height
                                            }
                                        >
                                            <TimeSeriesChart
                                                series={series}
                                                stacked={
                                                    controls.stacked.active &&
                                                    !hideStackedControls
                                                }
                                                navigator={controls.navigator}
                                                area={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.area
                                                }
                                                showTotal={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.showTotal
                                                }
                                                height={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.height
                                                }
                                                yAxis={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.yAxis
                                                }
                                                linePlotOptions={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.linePlotOptions
                                                }
                                                currencyValueDecimals={
                                                    mode.activeMode
                                                        .visualizationConfig
                                                        ?.currencyValueDecimals
                                                }
                                                legend={{
                                                    // Controls
                                                    enabled:
                                                        controls.legend.active,
                                                    // Config
                                                    ...(mode.activeMode
                                                        .visualizationConfig
                                                        ?.legend
                                                        ? mode.activeMode
                                                              .visualizationConfig
                                                              ?.legend
                                                        : {}),
                                                }}
                                            />
                                        </WindowResizeObserver>
                                    </RenderWhenVisible>
                                </TimeseriesCard>
                            );
                        }}
                    </TimeSeriesFetcher>
                );
            }}
        </TimeseriesStateManager>
    );
}
